import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { Asset, Booklet, Project, Status } from '../lib/types';
import { STATUS_LABEL } from '../lib/types';
import { Spinner, StatusPill, useToast } from '../components/ui';

const TRANSITIONS: Record<Status, Status[]> = {
  NOT_STARTED: ['IN_PROGRESS'],
  IN_PROGRESS: ['PENDING', 'REVIEW'],
  PENDING: ['IN_PROGRESS'],
  REVIEW: ['REVISION', 'APPROVED', 'IN_PROGRESS'],
  REVISION: ['IN_PROGRESS'],
  APPROVED: ['PUBLISHED', 'REVISION'],
  PUBLISHED: [],
};
const REVIEW_ONLY: Status[] = ['APPROVED', 'REVISION', 'PUBLISHED'];

const TRANSITION_LABEL: Partial<Record<Status, string>> = {
  REVIEW: 'Send for review',
  APPROVED: 'Approve',
  REVISION: 'Request changes',
  IN_PROGRESS: 'Back to writing',
  PENDING: 'Mark as waiting for prices',
};

export type ProjectContext = {
  project: Project;
  assets: Asset[];
  saving: boolean;
  /** false for reviewers and viewers — editors see a read-only notice instead */
  canEdit: boolean;
  update: (updater: (booklet: Booklet) => Booklet) => void;
  setProject: (project: Project) => void;
  reload: () => Promise<void>;
  reloadAssets: () => Promise<void>;
};

export const useProjectContext = () => useOutletContext<ProjectContext>();

/** Four steps, in the order people actually work. */
const STEPS = [
  ['import', '1', 'Import files'],
  ['content', '2', 'Text'],
  ['design', '3', 'Photos & pages'],
  ['preview', '4', 'Preview & PDF'],
] as const;

const HISTORY_LIMIT = 60;
const GROUP_MS = 1200;

export function ProjectLayout() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useAuth();
  const canEdit = can('edit');

  const [project, setProjectState] = useState<Project | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [saving, setSaving] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const timer = useRef<number | null>(null);
  const queued = useRef<Booklet | null>(null);

  // undo / redo
  const past = useRef<Booklet[]>([]);
  const future = useRef<Booklet[]>([]);
  const lastPush = useRef(0);
  const [, forceRender] = useState(0);

  const reload = useCallback(async () => {
    setProjectState(await api.getProject(id));
  }, [id]);

  const reloadAssets = useCallback(async () => {
    setAssets(await api.listAssets(id));
  }, [id]);

  useEffect(() => {
    past.current = [];
    future.current = [];
    void reload().catch((e) => toast.push((e as Error).message, true));
    void reloadAssets().catch(() => {});
  }, [reload, reloadAssets]);

  const queueSave = useCallback((content: Booklet) => {
    queued.current = content;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      const pending = queued.current;
      if (!pending) return;
      setSaving(true);
      try {
        const saved = await api.updateProject(id, { content: pending });
        const fresh = await api.preflight(id);
        setProjectState((p) => (p ? { ...p, status: saved.status, updatedAt: saved.updatedAt, issues: fresh.issues } : p));
      } catch (e) {
        toast.push(`Could not save: ${(e as Error).message}`, true);
      } finally {
        setSaving(false);
      }
    }, 700);
  }, [id, toast]);

  // The latest project, readable from callbacks without putting side effects
  // inside state updaters (StrictMode runs those twice).
  const projectRef = useRef<Project | null>(null);
  projectRef.current = project;

  const commit = useCallback((next: Project) => {
    projectRef.current = next;
    setProjectState(next);
    forceRender((n) => n + 1);
  }, []);

  /** Optimistic edit + debounced save, so typing never waits on the network. */
  const update = useCallback((updater: (booklet: Booklet) => Booklet) => {
    if (!canEdit) {
      toast.push('Your role can look and comment, but not edit', true);
      return;
    }
    const current = projectRef.current;
    if (!current) return;
    const content = updater(structuredClone(current.content));
    if (JSON.stringify(content) === JSON.stringify(current.content)) return;

    // typing bursts become one undo step
    const now = Date.now();
    if (now - lastPush.current > GROUP_MS) {
      past.current.push(current.content);
      if (past.current.length > HISTORY_LIMIT) past.current.shift();
    }
    lastPush.current = now;
    future.current = [];

    queueSave(content);
    commit({ ...current, content });
  }, [canEdit, commit, queueSave, toast]);

  const undo = useCallback(() => {
    const current = projectRef.current;
    const previous = past.current.pop();
    if (!current || !previous) return;
    future.current.push(current.content);
    lastPush.current = 0;
    queueSave(previous);
    commit({ ...current, content: previous });
  }, [commit, queueSave]);

  const redo = useCallback(() => {
    const current = projectRef.current;
    const next = future.current.pop();
    if (!current || !next) return;
    past.current.push(current.content);
    lastPush.current = 0;
    queueSave(next);
    commit({ ...current, content: next });
  }, [commit, queueSave]);

  // Ctrl+Z / Ctrl+Y — but leave text fields to their own native undo
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if (key === 'y' || (key === 'z' && e.shiftKey)) { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  const move = async (to: Status) => {
    try {
      await api.transition(id, to);
      await reload();
      toast.push(`Status: ${STATUS_LABEL[to]}`);
    } catch (e) {
      const err = e as Error & { data?: { issues?: Array<{ level: string; message: string }> } };
      const first = err.data?.issues?.find((i) => i.level === 'blocker');
      toast.push(first ? `${err.message}: ${first.message}` : err.message, true);
    }
  };

  const rename = async (name: string) => {
    setEditingName(false);
    if (!project || !name.trim() || name.trim() === project.name) return;
    try {
      const saved = await api.updateProject(id, { name: name.trim() });
      setProjectState((p) => (p ? { ...p, name: saved.name } : p));
    } catch (e) {
      toast.push((e as Error).message, true);
    }
  };

  const duplicate = async () => {
    if (!project) return;
    const name = window.prompt('Name for the copy', `${project.name} (copy)`);
    if (!name) return;
    try {
      const copy = await api.duplicateProject(project.id, name);
      toast.push(`Copied to "${copy.name}"`);
      navigate(`/projects/${copy.id}/content`);
    } catch (e) {
      toast.push((e as Error).message, true);
    }
  };

  const setProject = useCallback((p: Project) => {
    past.current = [];
    future.current = [];
    setProjectState(p);
  }, []);

  const context = useMemo<ProjectContext | null>(
    () => (project ? { project, assets, saving, canEdit, update, setProject, reload, reloadAssets } : null),
    [project, assets, saving, canEdit, update, setProject, reload, reloadAssets],
  );

  if (!context || !project) return <div className="row"><Spinner /> <span className="sub">Loading booklet…</span></div>;

  const next = TRANSITIONS[project.status].filter((to) => {
    if (REVIEW_ONLY.includes(to)) return can('review');
    if (to === 'REVIEW') return can('edit');
    return can('comment');
  }).filter((to) => to !== 'PUBLISHED'); // publishing happens from the preview page

  return (
    <>
      <div className="row project-head" style={{ marginBottom: 6 }}>
        <NavLink to="/projects" className="btn small ghost">← All booklets</NavLink>
        {editingName ? (
          <input
            className="title-input"
            autoFocus
            defaultValue={project.name}
            onBlur={(e) => void rename(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') setEditingName(false);
            }}
          />
        ) : (
          <h1 className={`title ${canEdit ? 'renamable' : ''}`} title={canEdit ? 'Click to rename' : undefined} onClick={() => canEdit && setEditingName(true)}>
            {project.name}
          </h1>
        )}
        <StatusPill status={project.status} />
        {canEdit && (saving ? <span className="row sub" style={{ gap: 6 }}><Spinner /> saving…</span> : <span className="sub">saved</span>)}
        <div style={{ flex: 1 }} />
        {canEdit && (
          <>
            <button className="btn small ghost" disabled={!past.current.length} onClick={undo} title="Undo (Ctrl+Z)">↶ Undo</button>
            <button className="btn small ghost" disabled={!future.current.length} onClick={redo} title="Redo (Ctrl+Y)">↷ Redo</button>
          </>
        )}
        {next.map((to) => (
          <button key={to} className={`btn small ${to === 'APPROVED' || to === 'REVIEW' ? 'primary' : ''}`} onClick={() => void move(to)}>
            {TRANSITION_LABEL[to] ?? STATUS_LABEL[to]}
          </button>
        ))}
        <NavLink to="history" className={({ isActive }) => `btn small ${isActive ? 'active-link' : 'ghost'}`}>History</NavLink>
        {canEdit && <button className="btn small ghost" onClick={() => void duplicate()}>Copy</button>}
      </div>

      {!canEdit && (
        <div className="notice" style={{ margin: '8px 0 12px' }}>
          You can view this booklet{can('comment') ? ', leave comments and approve it' : ' and download its PDFs'} — editing is for editors.
        </div>
      )}

      <div className="tabs steps">
        {STEPS.filter(([path]) => canEdit || path === 'preview').map(([path, n, label]) => (
          <NavLink key={path} to={path} className={({ isActive }) => (isActive ? 'active' : '')}>
            <span className="step-n">{n}</span> {label}
          </NavLink>
        ))}
      </div>

      <Outlet context={context} />
    </>
  );
}
